import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AlertCamera } from 'features/cameras/cameras.types';
import { UseAlertCameraPlayerStateReturn } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { CommandPaletteAction } from 'components/CommandPaletteDialog/commandPaletteAction.types';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';

export const useCameraCommandPaletteActions = (
  currentCameraId: string,
  relatedCameras: AlertCamera[],
  isFullscreen: boolean = false,
): void => {
  const { t } = useTranslation();
  const history = useHistory();

  const cameraShortcuts: Array<CommandPaletteAction> = relatedCameras.map(
    (camera, index) => ({
      category: 'camera',
      description: camera.name,
      keys: `${index + 1}`,
      handler: () => {
        const pathExtra = isFullscreen ? '/fullscreen' : '';
        history.push(`/camera/${camera.id}${pathExtra}`);
      },
    }),
  );

  cameraShortcuts.push({
    category: 'camera',
    description: t('keyboardShortcuts.cameras.toggleFullScreen'),
    keys: 'f',
    handler: () => {
      history.push(
        isFullscreen
          ? `/camera/${currentCameraId}`
          : `/camera/${currentCameraId}/fullscreen`,
      );
    },
  });

  useKeyboardShortcuts(cameraShortcuts, [
    currentCameraId,
    relatedCameras.length,
  ]);
};

export const useCameraPlayerCommandPaletteActions = ({
  alertCameraPlayerState,
  handleTogglePlay,
  handleStopTimelapse,
  adjustReplay,
}: {
  alertCameraPlayerState: UseAlertCameraPlayerStateReturn;
  handleTogglePlay: () => void;
  handleStopTimelapse: () => void;
  adjustReplay: (replayTime: number, increase: boolean) => number;
}): void => {
  const { t } = useTranslation();
  const {
    setTimelapseFrameNumber,
    setTimelapseReplayTime,
    playerStatus,
    timelapseFrameNumber,
    timelapseReplayTime,
  } = alertCameraPlayerState;
  useKeyboardShortcuts(
    [
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.playPause'),
        keys: 'space',
        handler: () => {
          handleTogglePlay();
        },
      },
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.stop'),
        keys: 's',
        handler: () => {
          handleStopTimelapse();
        },
      },
    ],
    [playerStatus],
  );
  useKeyboardShortcuts(
    [
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.nextFrame'),
        keys: 'right',
        handler: () => {
          setTimelapseFrameNumber(timelapseFrameNumber + 1);
        },
      },
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.previousFrame'),
        keys: 'left',
        handler: () => {
          setTimelapseFrameNumber(timelapseFrameNumber - 1);
        },
      },
    ],
    [timelapseFrameNumber],
  );
  useKeyboardShortcuts(
    [
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.increaseReplayTime'),
        keys: 'up',
        handler: () => {
          setTimelapseReplayTime(adjustReplay(timelapseReplayTime, true));
        },
      },
      {
        category: 'camera',
        description: t('keyboardShortcuts.cameras.decreaseReplayTime'),
        keys: 'down',
        handler: () => {
          setTimelapseReplayTime(adjustReplay(timelapseReplayTime, false));
        },
      },
    ],
    [timelapseReplayTime],
  );
};
